"use client";

import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from "react";
import { trackFunnel } from "@/libs/analytics";

export type EditorV2HealthProps = {
  tool_id: string;
  catalog_tool_id?: string;
  tool_name?: string;
  editor_version: "v2";
};

type EditorV2HealthContextValue = {
  props: EditorV2HealthProps;
  track: (step: string, extra?: Record<string, unknown>) => void;
  markGenerateStarted: () => void;
  generateDurationMs: () => number | undefined;
};

const EditorV2HealthContext = createContext<EditorV2HealthContextValue | null>(null);

export function EditorV2HealthProvider({
  toolId,
  catalogToolId,
  toolName,
  children,
}: {
  toolId: string;
  catalogToolId?: string;
  toolName?: string;
  children: ReactNode;
}) {
  const generateStartedAt = useRef<number | null>(null);

  const props = useMemo<EditorV2HealthProps>(
    () => ({
      tool_id: toolId,
      ...(catalogToolId ? { catalog_tool_id: catalogToolId } : {}),
      ...(toolName ? { tool_name: toolName } : {}),
      editor_version: "v2",
    }),
    [toolId, catalogToolId, toolName]
  );

  const track = useCallback(
    (step: string, extra: Record<string, unknown> = {}) => {
      trackFunnel("tool_engagement", step, { ...props, ...extra });
    },
    [props]
  );

  const markGenerateStarted = useCallback(() => {
    generateStartedAt.current = performance.now();
  }, []);

  const generateDurationMs = useCallback(() => {
    if (generateStartedAt.current == null) return undefined;
    return Math.round(performance.now() - generateStartedAt.current);
  }, []);

  const value = useMemo(
    () => ({ props, track, markGenerateStarted, generateDurationMs }),
    [props, track, markGenerateStarted, generateDurationMs]
  );

  return <EditorV2HealthContext.Provider value={value}>{children}</EditorV2HealthContext.Provider>;
}

export function useEditorV2Health(): EditorV2HealthContextValue | null {
  return useContext(EditorV2HealthContext);
}
